import type { PublicAuthor } from '@dove/shared';
import { BirdPortrait } from '@/components/BirdPortrait';
import { ToProfile } from '@/components/ToProfile';
import { Written } from '@/components/Written';
import { cn } from '@/lib/utils';
import { LetterStamp } from './LetterStamp';

/**
 * A letter. THE letter — there is one of these now, and every surface that shows
 * someone's writing renders it.
 *
 * WHAT THIS REPLACED, because the duplication was visible to users rather than
 * merely untidy. Two components drew a letter and disagreed about what a letter
 * looks like: the Loft drew square, full-bleed, sans-serif previews clamped to
 * three lines; a person's card drew `rounded-2xl` serif sheets inset in a column.
 * Same object, two materials, and you could see both within one tap of each
 * other. Neither was wrong in isolation, which is exactly why it survived
 * review — the bug only exists in the comparison.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PAPER IS GONE, AND THAT IS THE MOST RECENT CHANGE TO THIS FILE (docs/5
 * § D260803-0020). A sheet is now the app's CARD SURFACE: `--card`, rounded at the card
 * radius, no hairline, held by a shadow. Warm cream stock, square corners and a
 * ring drawn in the ink were one device wearing three costumes, and all three are
 * out together, because taking any one of them away on its own leaves a sheet
 * that looks like a mistake — square corners on a white card read as an
 * unstyled div, and a cream card with rounded corners reads as a tinted panel.
 *
 * WHAT THE PAPER WAS DOING, AND WHAT DOES IT NOW. The material's whole job was to
 * say *this is not a chat bubble* — docs/1 spends its Letters section insisting a
 * letter is not a message, and the texture was the visual half of that claim.
 * Everything ELSE that made the claim survives untouched, and between them they
 * are the stronger half:
 *
 *   - **A serif body at a reading measure.** 17px on a 1.75 leading, held to
 *     34rem (`letter-sheet` in index.css). Nothing else in the product is set
 *     like this, and it is the difference between reading a page and reading a
 *     row.
 *   - **It runs to a full width rather than shrink-wrapping its text.** A bubble
 *     is as wide as its sentence; a four-word letter here is still a whole sheet
 *     with four words on it.
 *   - **A letterhead, a stamp and a number.** A card with postage on it and
 *     `Letter 12` in the corner is not something any messenger has ever drawn.
 *   - **No tail, ever.** See NOT THE FOURTH below, which is the one bullet of
 *     that list this change did not touch.
 *
 * The honest cost, recorded rather than glossed: a letter and a post are now
 * drawn on the same surface as the app's chrome, so "somebody wrote this" is no
 * longer legible from the material alone at arm's length. What replaces that
 * signal on the surface it mattered most — the Loft's list, where tall letter
 * cards were being mistaken for the thread itself — is that the list stopped
 * being made of sheets at all (see `CorrespondenceRow`, docs/5 § D260803-0021).
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * THE SETTLEMENT THAT MADE THIS ONE COMPONENT, and the reasoning for each half
 * of it. Two of these are about the material and are now history; they are kept
 * because the third and fourth still stand on them.
 *
 *   - **Square corners.** HISTORY — see above. The argument was that a rounded
 *     rectangle is the shape of every chat bubble ever drawn and a sheet of paper
 *     has corners. It was right about the shape of a chat bubble and wrong that a
 *     corner was the cheapest way to refuse it.
 *   - **Inset from the screen edges, not full-bleed.** STANDS, and it is the one
 *     the material's departure does not touch. The Loft's sheets used to run edge
 *     to edge, which made them structural — bands of the screen rather than
 *     things lying on it. Held off the edge by the same 16px gutter the rest of
 *     the app uses, a sheet is an object with a background behind it.
 *   - **A serif body, a sans letterhead.** The serif is the product rule; the
 *     metadata is chrome and stays in the UI face. The Loft's old sans preview
 *     argued a typeface change was decoration on top of paper. On a full-bleed
 *     band it was. On an inset sheet it is the difference between reading a page
 *     and reading a row.
 *   - **More letter, not more rows.** Five clamped lines rather than three. The whole
 *     argument for a tall Loft row is that a letter is worth reading in place, and
 *     three lines is a chat inbox with better manners. Two sheets on a phone that
 *     you can actually read beats four you cannot.
 *
 * THE BYLINE IS PART OF THE LETTER, NOT A ROW ABOVE IT. Name, date, bird and
 * stamp are inside the sheet, on the paper, because that is where a letter puts
 * them — a letterhead and a postmark are features of the document. Hoisting them
 * into a container's header was what made the Loft feel like an inbox listing
 * letters instead of a table with letters on it.
 *
 * THE LETTERHEAD IS TWO LINES AND IT USED TO BE THREE (docs/5 § D260802-0014). A round
 * photograph, then the name, then the time under it, then the pair's stage and
 * the month it started under that — three stacked lines of chrome above five lines
 * of letter, each saying one small thing. That is the failure mode a letterhead
 * has: it is the only part of a sheet that is not the letter, so every line it
 * spends is a line of somebody's writing pushed under the fold. What it is now:
 *
 *   - **Line one is who and when**, on one baseline: the sender's bird, the name,
 *     and the date. `Miriam · Yesterday` is one fact read left to right, and it
 *     costs a line where a name over a timestamp costs two.
 *   - **Line two is where the two of you are, and which letter this is.** The
 *     stage was prose (`Letters · since May`); it is now the same `Chip` the card
 *     draws that fact with, so a level looks like a level everywhere in the app.
 *     `since May` is gone from the sheet: a date the correspondence started is a
 *     fact about the pair rather than about this letter, and it is still on the
 *     reader's own header, one tap away.
 *   - **The number, because letters have numbers.** Numbering a letter is what
 *     letters have always done, and it is the one piece of chrome that makes a
 *     sheet feel like the twelfth of something rather than a message with a
 *     serif. It is an ordinal, not a tally — see `LoftEntry.letterNumber`, which
 *     carries the whole argument for why the product may say `Letter 12` while
 *     refusing to say `12 letters`.
 *
 * THE BIRD IS BESIDE THE NAME AND THE PHOTOGRAPH IS ON THE STAMP, which is the
 * other half of the same change and is argued in `LetterStamp`. Between them the
 * sheet draws each person exactly once, in each of the two places docs/1 asks
 * for: a bird beside a name, and postage on a letter.
 *
 * THE LETTERHEAD IS THE PERSON, AND IT GOES TO THEIR CARD. The name (with the
 * bird, which is a rendering of the same person) and the portrait on the stamp
 * are both links to `/u/:handle` — the rule `PostCard` states and this surface
 * was the last one breaking: in the Loft a letterhead was inert, so the only
 * thing a correspondent's face and name did was open the letters you were
 * already looking at, and reaching the person meant opening the correspondence
 * to use the header at the top of it. There is no people search anywhere in this
 * product, so a name that does not link is a dead end (`ToProfile` argues it).
 *
 * WHICH IS WHY `open` IS LAID OVER THE SHEET RATHER THAN WRAPPED AROUND IT. The
 * whole readable region still opens the correspondence, and it used to do that by
 * wrapping letterhead and body in one anchor — which leaves nowhere to put a
 * second one, because an anchor inside an anchor is invalid and resolves to
 * whichever the browser prefers. So the caller's link is a transparent overlay at
 * `z-10` filling the readable region, and the two identity links sit above it at
 * `z-20`: no nesting, every part of the card keeps the destination it had, and
 * the name and the stamp take the one they should always have had.
 *
 * YOURS AND THEIRS ARE NOW DRAWN APART, WHICH REVERSES WHAT THIS FILE USED TO
 * SAY, so the old position and why it lost are both recorded here.
 *
 * The rule was: identical sheets, no alignment, no tint, no tail — the byline
 * says who wrote it and that is enough, because every device for distinguishing
 * sender from recipient is borrowed from chat and a letter is not a message. The
 * reasoning was sound about WHERE the devices come from and wrong about what the
 * reader is doing. A correspondence is now rendered as a running thread, and in
 * a column of same-shaped sheets the only thing separating your voice from
 * theirs is a name in 15px at the top of each one. You do not read a name; you
 * scan a shape, and there was no shape to scan, so following who said what meant
 * re-reading the letterhead on every sheet.
 *
 * SO THREE SIGNALS, AND NOT THE FOURTH:
 *
 *   - **A second surface.** `--paper-sent` — the same card, one step off the
 *     page, so the two read as two surfaces rather than as one surface with a
 *     state on it. index.css argues the value, and notes what it lost when the
 *     paper went: it used to be a hue rotation, which is a device only a
 *     *material* can use. Note this is a real background change and NOT the
 *     `opacity-95` an earlier attempt used, which was both invisible as a signal
 *     and enough to put the sheet on its own compositing layer — that showed up
 *     in light mode as a seam across the sheet.
 *   - **A mirrored letterhead.** Face and stamp swap ends and the metadata sets
 *     to the other margin, so the top edge of a sent sheet is a different
 *     silhouette from a received one at a glance, with the type still legible.
 *   - **An offset gutter**, which belongs to the caller rather than to this
 *     component: `Correspondence` insets the two sides from opposite margins, so
 *     the ragged edge of the column tells you whose turn a sheet is before you
 *     have focused on it.
 *
 * NOT THE FOURTH: NO TAIL, NO BUBBLE. This bullet used to open "no rounded
 * corners" and it no longer can — the sheet is a rounded card. What it still is:
 * a page, serif, at a reading measure, running to a full width rather than
 * shrink-wrapping its text, which is the actual difference between a letter that
 * happens to be aligned and a chat bubble. A tail is the one device with no
 * meaning outside chat, so it is the one device that stays out, and it is a
 * harder line now that the corners have gone: a rounded, aligned, tinted card
 * with a tail on it would BE a chat bubble, and nothing else on this list would
 * be left to say otherwise.
 *
 * THE BODY IS NOT RIGHT-ALIGNED ON A SENT SHEET, and that is not an oversight.
 * A message is one line and can be set from either margin; a letter is
 * paragraphs, and ragged-left prose is measurably slower to read because every
 * line starts somewhere new. The flip is carried by the letterhead and the
 * gutter, which cost nothing, and the prose keeps the alignment its script asks
 * for — `dir="auto"`, so an Arabic letter sets from the right on both sides.
 *
 * NOTHING HERE REPORTS ON THE OTHER PERSON. No read state of theirs, no presence,
 * no "waiting". See `lib/letter-turn`, which owns that rule and every label that
 * touches it.
 */

/*
 * THERE IS NO CLAMP ON A SHEET ANY MORE, and the machinery went with it: no
 * `clamp` prop, no `-webkit-line-clamp`, no gradient fading the last line into
 * the surface, and no `useOverflowing` measuring whether there was more
 * underneath. All of it existed for ONE call site — the Loft's card, five lines
 * of the last letter with a fold at the bottom — and that card is now a row that
 * quotes nothing (docs/5 § D260803-0021).
 *
 * The argument the old note made is worth keeping in one sentence, because it is
 * the thing to re-derive rather than re-discover if a clamped letter is ever
 * wanted again: clamp on the LINE BOX (`line-clamp-N`), never on a `max-height`
 * in `em`, because a height clip has no idea where the lines are and cannot
 * punctuate — the browser's own ellipsis is what says "this continues" to
 * somebody with the gradient turned off, inverted, or replaced by a
 * high-contrast theme.
 *
 * `attribution` went at the same time and for the same reason: a bold "You
 * wrote — " lead-in only made sense on a card that was a CORRESPONDENCE headed by
 * the other person. On a sheet the letterhead names the author, which is the
 * whole job of a letterhead.
 */

/**
 * What an `open` link or button wears: a transparent sheet laid OVER the readable
 * region, carrying that region's hover wash and its focus ring.
 *
 * Handed to the caller rather than exported as a class it has to remember, so the
 * only thing a call site supplies is the destination. Shared with `Envelope`,
 * which is the same object sealed and takes the same tap.
 */
export const LETTER_OPEN =
  /* `ring-inset`, because the ring is on a box that fills the card: an outset one
     is drawn past the paper's edge and an `Envelope` clips it away entirely. */
  'absolute inset-0 z-10 outline-none transition-colors hover:bg-paper-ink/[0.03] focus-visible:ring-3 focus-visible:ring-inset focus-visible:ring-ring/50';

/**
 * The two identity links, which sit ABOVE that overlay. `z-20` is the whole
 * trick — see the note at the top of the file.
 *
 * `z-20` IS LOCAL TO ONE CARD AND MUST STAY THAT WAY. It means "above this card's
 * own open-overlay", which is the only comparison it is ever entitled to make — so
 * the card that carries it declares `isolate`, and both of these numbers are then
 * scoped to that stacking context by construction. See `LETTER_SURFACE` below for
 * the whole argument and the bug that produced it.
 */
export const LETTER_TO_CARD =
  /* No `position` of its own: `z-20` needs one, and the two call sites differ —
     a letterhead's name sits in flow and takes `relative`, an envelope's stamp is
     already `absolute` on the flap. Baking `relative` in here would have left
     that one relying on `twMerge` to drop it again. */
  'z-20 rounded-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50';

/**
 * THE CARD A LETTER IS DRAWN ON — surface, radius, shadow, and the one property
 * that keeps its insides from leaking out. Shared with `Envelope`, which is the
 * same object sealed and must be the same card in every respect.
 *
 * `rounded-xl` IS THE DESIGN SYSTEM'S CARD RADIUS, and it used to be
 * `rounded-none` with a paragraph explaining that the squareness was load-bearing.
 * It was, for as long as the sheet was pretending to be paper — see the note at
 * the top of the file, and docs/5 § D260803-0020. `ui/card` is `rounded-xl`, so a letter is
 * now the radius everything else in the app draws a surface at.
 *
 * `overflow-hidden` FOLLOWS FROM THE RADIUS and is not decoration: the footer's
 * top rule runs the full width of the card, so it would cut across a rounded
 * corner without it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `isolate` IS THE LOAD-BEARING ONE, AND IT IS A BUG FIX RATHER THAN A TIDY-UP.
 *
 * A letter's insides are layered: `LETTER_OPEN` is a transparent overlay at
 * `z-10` over the readable region, and the name and the stamp sit above it at
 * `z-20` so they keep their own destination. Both numbers mean something *within
 * one card* and nothing outside it.
 *
 * Without a stacking context on the card they were not within one card. They were
 * declared in whatever context the card happened to land in — which, in the
 * reader at `/loft/$handle`, is the same context as the screen's sticky header.
 * That header is also `z-20`, and the letters come after it in the DOM, so a
 * stamp and a pigeon scrolling up the page painted straight OVER the header:
 * somebody's photograph on top of the name and the back button.
 *
 * THE BANDAID WOULD HAVE BEEN TO PUT THE HEADER ON `z-30`. That fixes the
 * screenshot and keeps the actual defect, which is that a private detail of how a
 * card layers itself is being compared against app chrome at all. The next
 * `z-40` inside a letter would win again, and the fix would be `z-50` on the
 * header.
 *
 * `isolation: isolate` makes the card a stacking context, so `z-10` and `z-20`
 * are resolved INSIDE it and cannot be compared with anything outside it — at any
 * value, including ones nobody has written yet. The card itself then paints as a
 * positioned element with `z-index: auto`, which is below any chrome carrying a
 * positive `z-index`. So the guarantee is "chrome wins because it is chrome",
 * not "chrome wins because its number is currently bigger".
 *
 * Note what this did NOT need: no number changed anywhere. If a change to this
 * line ever has to be paired with a bump on a header, the bump is the mistake.
 * `Correspondence` isolates the thread as a whole for the same reason, one level
 * up, and says so.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const LETTER_SURFACE =
  'letter-sheet relative isolate w-full overflow-hidden rounded-xl shadow-sm';

export function LetterSheet({
  author,
  mine = false,
  dateLabel,
  postmark,
  number,
  body,
  edited = false,
  editing,
  menu,
  footer,
  open,
  className,
  ...props
}: Omit<React.ComponentProps<'article'>, 'children'> & {
  /** Whose letterhead this is: the name, the bird beside it and the portrait on
   *  the stamp. */
  author: PublicAuthor;
  /** Written by the viewer, so the byline reads "You". */
  mine?: boolean;
  dateLabel: string;
  postmark?: string;
  /**
   * Which letter this is in the correspondence, counted from the first.
   *
   * Optional, and absent means the sheet says nothing rather than guessing: a
   * component holding one letter cannot know its position, and `Letter 1` on
   * every sheet would be a confident lie. See `LoftEntry.letterNumber` for why an
   * ordinal is allowed here at all when a count is not.
   */
  number?: number;
  body: string;
  /**
   * Rewritten after it landed (docs/5 § D260805-0418), drawn as `(edited)` after
   * the date.
   *
   * BESIDE THE DATE, WHICH IS THE ONE LINE OF A LETTERHEAD THAT IS ABOUT WHEN. A
   * letter is dated, and "this is not the version that arrived" is a fact of the
   * same kind — so it costs no line, and it is nowhere near the prose.
   *
   * ON BOTH SIDES OF THE THREAD, unlike `queued` and `unread`. The mark says the
   * words changed after somebody could read them, and the person who changed them
   * is exactly who should see that their sheet now carries it.
   */
  edited?: boolean;
  /**
   * The words in a field instead of on the page — `ReviseField`, when the author
   * is rewriting this letter.
   *
   * A SLOT RATHER THAN A FLAG, because the editor belongs to whoever knows how to
   * save: this component holds a letter, and a component holding a letter has no
   * business knowing which endpoint takes an amendment for it. `body` is still
   * required, so a sheet cannot exist without the words it is a sheet of.
   */
  editing?: React.ReactNode;
  /**
   * The author's own overflow menu — `ReviseMenu`, at the end of the letterhead.
   *
   * IN THE LETTERHEAD AND NOT IN THE FOOTER, which is where `PostCard` puts the
   * same control, and the difference is worth stating because consistency is the
   * rule it bends. A sheet's footer is a hairline plus a 3rem row that exists only
   * under the NEWEST letter, to hold the one control a letter offers; giving every
   * letter of your own a footer to hold a 28px button would add a rule and a band
   * of air to each one, and the thread is a column of sheets where that reads as a
   * change to what a letter looks like. The letterhead already has a row with room
   * at the end of it.
   */
  menu?: React.ReactNode;
  /*
   * There is no `unread` here any more. A sheet is a letter you have OPENED —
   * an unopened one is an `Envelope`, which is a different component because it
   * is a different object, and the bolder name plus a dot that used to live on
   * this prop was a mark on an already-spread-open letter. That state moved
   * wholesale; nothing here reports it.
   */
  /*
   * There is no `meta` slot here either. It was extra letterhead detail on the
   * second line and the Loft's card was its only user, putting the pair's level
   * chip in it; the level is on the row now (docs/5 § D260803-0021). Its old argument
   * still holds against putting one back on a THREAD, which is where sheets
   * actually live: a level belongs to a pair, and today's level on all twelve
   * sheets of a scroll would be stamping it on last February's letter.
   */
  /** Actions on the sheet's bottom edge, typically `RespondAction`. */
  footer?: React.ReactNode;
  /**
   * Where the READABLE REGION goes — letterhead and body, never the footer — so
   * the card can be a link to something fuller.
   *
   * IT IS HANDED A CLASS AND RETURNS THE LINK, rather than being handed the
   * content to wrap, and the reason is the letterhead: the name and the stamp are
   * links to the sender's card, and an anchor inside an anchor is invalid HTML
   * that resolves in practice to whichever target the browser prefers. Laid over
   * the region instead, the card's link is a sibling of the identity links rather
   * than their parent — so `LETTER_OPEN` positions it and the caller supplies
   * nothing but the destination and the label.
   *
   * A render prop and not an `href` because routing stays out of this file for
   * the DESTINATION: it is a dynamic route whose params only the caller knows,
   * and one call site opens a seal in place rather than navigating at all. The
   * sender's card is the opposite case — this component already holds the handle,
   * so it links there itself. See `ToProfile`.
   */
  open?: (className: string) => React.ReactNode;
}) {
  const readable = (
    <>
      {/* The letterhead. `font-sans` and its own type scale: `letter-sheet` sets
          a 17px serif for prose, which is wrong for metadata.

          `flex-row-reverse` on a sent sheet, which is the whole of the mirror:
          face and stamp are the two ends of this row, so reversing it swaps them
          without either one needing to know where it sits. Logical by
          construction — the row is already laid out in the writing direction, so
          this flips relative to the script rather than relative to the screen,
          and needs no `rtl:` twin. */}
      <header
        className={cn(
          'flex items-start gap-3 px-4 pt-3.5 font-sans',
          mine && 'flex-row-reverse',
        )}
      >
        {/* `text-end` on the column, which is the other half of the mirror: the
            name, the date row and the row beneath it all set to the far margin
            together, and each stays in its own reading order. */}
        <div className={cn('min-w-0 flex-1', mine && 'text-end')}>
          {/* WHO AND WHEN, ON ONE BASELINE. `justify-end` rather than another
              `flex-row-reverse`, for the reason the row below gives: reversing
              would put the date in front of the name. */}
          <div
            className={cn('flex items-center gap-1.5', mine && 'justify-end')}
          >
            {/* WHO WROTE IT, AND A LINK TO THEM. The bird is INSIDE the link
                with the name rather than beside it — it is a rendering of the
                same person, and splitting them would leave a 21px glyph as a
                dead zone in the middle of the tap target. `PostCard`'s byline is
                built the same way and says the same thing. */}
            <ToProfile
              author={author}
              aria-label={mine ? 'Open your card' : `Open ${author.name}’s card`}
              className={cn(
                LETTER_TO_CARD,
                'relative flex min-w-0 items-center gap-1.5 hover:underline',
              )}
            >
              {/* The sender's own bird, beside their name, which is where docs/1
                  § Your pigeon puts it. Flipped to look back at the name it
                  belongs to — the rule every byline in the product follows
                  (docs/5 § D260805-1523) — and mirrored again under RTL. */}
              <BirdPortrait
                bird={author.bird}
            plumage={author.plumage}
                aria-hidden
                className="w-[21px] shrink-0 -scale-x-100 rtl:scale-x-100"
              />

              <span className="min-w-0 truncate text-[15px] font-medium text-paper-ink">
                {/* In words. There are only two possible answers and the reader
                    is one of them, so a name and "You" is the whole of it. `bdi`
                    because a display name is user data in an unknown script
                    (docs/8 § G). */}
                {mine ? 'You' : <bdi>{author.name}</bdi>}
              </span>
            </ToProfile>

            {/* The name truncates and the date does not: a long display name may
                lose its tail, but four characters of date are the difference
                between a dated letter and an undated one. */}
            <span aria-hidden className="shrink-0 text-[12px] text-paper-ink/45">
              ·
            </span>
            <time className="shrink-0 text-[12px] text-paper-ink/65">
              {dateLabel}
            </time>

            {/* No separating dot: the brackets do that, and this row already has
                one. Same mark, same wording and same weight as a post's byline
                carries (`PostCard`), because it is the same fact about the same
                object at a different size. */}
            {edited && (
              <span className="shrink-0 text-[12px] text-paper-ink/65">
                (edited)
              </span>
            )}

            {/* `ms-auto` so the menu takes the end of the row whichever way the
                letterhead is facing, instead of trailing the date. */}
            {menu && <span className="ms-auto flex shrink-0">{menu}</span>}
          </div>

          {/* Where the two of you are, which letter this is, and where it was
              posted. Every part is optional and an empty row collapses.

              `justify-end` rather than another `flex-row-reverse`: this row
              wraps, and reversing a wrapping row reverses within each line, so
              "Letter 3 · Manchester" would have come out as the postmark first.
              Alignment is all the mirror needs here; the reading order is the
              one the letterhead has always had. */}
          {(number !== undefined || postmark) && (
            <div
              className={cn(
                'mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-paper-ink/60',
                mine && 'justify-end',
              )}
            >
              {number !== undefined && (
                /* `tabular-nums` so a thread numbered 9, 10, 11 does not jitter
                   down the column. Said in words rather than as a bare numeral:
                   "12" alone in a letterhead is a quantity of something. */
                <span className="font-medium tabular-nums text-paper-ink/70">
                  Letter {number}
                </span>
              )}
              {postmark && (
                <>
                  <span aria-hidden className="text-paper-ink/40">
                    ·
                  </span>
                  {/* A postmark is a city and nothing finer, the same rule as
                      banded distance (docs/8 § E). */}
                  <span>{postmark}</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* THE PORTRAIT GOES WHERE THE NAME GOES. It is the only photograph on
            a sheet (docs/5 § D260802-0014 put it on the stamp), so it is the thing a
            thumb reaches for, and it would be the one avatar in the product
            that led nowhere.

            Redundant to a screen reader, though — the name link above it is the
            same destination in words — so it is hidden from the tree and taken
            out of the tab order rather than announced twice. Safe to hide
            because it is unreachable by keyboard: nothing focusable is buried
            under `aria-hidden`. */}
        <ToProfile
          author={author}
          aria-hidden
          tabIndex={-1}
          className={cn(LETTER_TO_CARD, 'relative shrink-0')}
        >
          <LetterStamp
            author={author}
            /* Franked because it travelled — set on letters that arrived, never
               on your own. A fact about the post, not about the person. */
            cancelled={!mine}
            className="-mt-1"
          />
        </ToProfile>
      </header>

      {/* THE LETTER. `whitespace-pre-wrap` because paragraph breaks are the
          author's and a letter has them — no longer conditional, since there is
          no clamped variant to keep newlines out of (see the note above).

          THE EDITOR TAKES THE SAME BOX when it is handed one: same padding, same
          serif, same measure, so rewriting a letter does not reflow the sheet
          around the words being rewritten. */}
      {editing ? (
        <div className="px-4 pt-2.5 pb-4">{editing}</div>
      ) : (
        <p
          /* `dir="auto"`: a letter is free text in an unknown script. */
          dir="auto"
          className="px-4 pt-2.5 pb-4 whitespace-pre-wrap"
        >
          {/* Names are tappable here as they are on an open letter, and the two
              sizes of the one object stay one object (docs/5 § D260809-0021).
              What a direct letter does NOT do is notify: `lib/mentions` on the
              server is only reached by public writing, because a notification
              saying somebody was named would be reporting the contents of a
              correspondence to a third party. */}
          <Written body={body} />
        </p>
      )}
    </>
  );

  return (
    <article
      /* `LETTER_SURFACE` is the card: surface, radius, shadow, and the `isolate`
         that keeps this sheet's internal `z-10`/`z-20` from competing with app
         chrome. It is shared with `Envelope` and argued at length above.

         NO HAIRLINE. The ring was the sheet's edge when the sheet was paper — a
         rule drawn in the ink so it survived both schemes — and it is what "no
         borders, a card surface" removes (docs/5 § D260803-0020). The shadow holds the edge
         now, which is how every other card in the app is held. */
      className={cn(
        LETTER_SURFACE,
        /* The other surface. One class, and the stamp follows it. */
        mine && 'letter-sheet-sent',
        className,
      )}
      {...props}
    >
      {/* The readable region and, over it, the link that opens it. `relative` so
          the overlay is bounded by letterhead and body and stops at the footer,
          which holds its own button. */}
      <div className="relative">
        {readable}
        {open?.(LETTER_OPEN)}
      </div>
      {footer}
    </article>
  );
}
